/**
 * Headless state for the package catalogue: the `GET /api/admin/plans/all` fetch
 * (plans + entitlement catalog + products), the service filter with everything
 * derived from it, and the delete flow with its error-code mapping. Returns no
 * JSX - web_admin and web_crm render their own chrome on this shape.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { makeT } from "../i18n";
import type {
  ApiClient, EntitlementCatalogEntry, Locale, PlanDTO, PlansAllResponse, ProductDTO,
} from "../types";
import { PLATFORM_PRODUCT_CODE, errCodeStr } from "./shared";

export interface UsePlansCatalogOptions {
  api: ApiClient;
  locale: Locale;
  /**
   * Scope the catalogue to ONE service (the service detail view): the filter pills stay
   * hidden, a new package defaults to this service and the editor locks the service picker.
   */
  lockedProductCode?: string;
}

export interface UsePlansCatalogResult {
  /** Packages after the service filter - what the chrome lists. */
  plans: PlanDTO[];
  /** Every package the API returned, unfiltered (pill counts, totals). */
  allPlans: PlanDTO[];
  /** The platform catalog. */
  catalog: EntitlementCatalogEntry[];
  /** The monitoring catalog (the Veebihaldus packages); empty from an API that predates it. */
  monitorCatalog: EntitlementCatalogEntry[];
  products: ProductDTO[];
  productByCode: Map<string, ProductDTO>;
  loading: boolean;
  reload: () => Promise<void>;
  /** User-picked service pill; `null` = all services. */
  productFilter: string | null;
  setProductFilter: (code: string | null) => void;
  /** The filter actually applied: a locked service wins over the pill. */
  effectiveFilter: string | null;
  /** Render the service pills? (Hidden when the view is locked to one service.) */
  showPills: boolean;
  /** Show the service name on a package card? (Only when unfiltered and several services exist.) */
  showProductOnCards: boolean;
  /** Service a newly created package belongs to: locked -> filtered -> platform. */
  newPlanProduct: string;
  /** How many packages a service owns (the count badge on a pill). */
  planCountFor: (productCode: string) => number;
  /** Package waiting for delete confirmation; `null` = the confirm dialog is closed. */
  deleteTarget: PlanDTO | null;
  setDeleteTarget: (plan: PlanDTO | null) => void;
  /** Open the confirm dialog for `plan` and clear any previous error. */
  askDelete: (plan: PlanDTO) => void;
  deleting: boolean;
  /** Translated message for the last failed delete, or `null`. */
  deleteErr: string | null;
  doDelete: () => Promise<void>;
}

export function usePlansCatalog({ api, locale, lockedProductCode }: UsePlansCatalogOptions): UsePlansCatalogResult {
  const t = makeT(locale);
  const [data, setData] = useState<PlansAllResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<PlanDTO | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);
  // Active service filter (null = every service); ignored while `lockedProductCode` is set.
  const [productFilter, setProductFilter] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.get<PlansAllResponse>("/admin/plans/all"));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function askDelete(plan: PlanDTO) {
    setDeleteErr(null);
    setDeleteTarget(plan);
  }

  async function doDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteErr(null);
    try {
      await api.del(`/admin/plans/${deleteTarget.code}`);
      setDeleteTarget(null);
      await reload();
    } catch (e) {
      const code = errCodeStr(e);
      setDeleteErr(
        code === "plan_in_use" ? t("editor.err.inUse")
        : code === "plan_on_sites" ? t("editor.err.inUseSites")
        : code === "default_plan_protected" ? t("editor.err.defaultProtected")
        : t("editor.err.generic"),
      );
    } finally {
      setDeleting(false);
    }
  }

  const allPlans = data?.plans ?? [];
  const catalog = data?.catalog ?? [];
  const monitorCatalog = data?.monitorCatalog ?? [];
  const products = data?.products ?? [];
  const productByCode = useMemo(() => new Map(products.map((p) => [p.code, p])), [products]);
  // A locked service beats the filter (detail view); otherwise the pill the user picked.
  const effectiveFilter = lockedProductCode ?? productFilter;
  const showPills = !lockedProductCode && products.length > 0;
  const showProductOnCards = productFilter === null && products.length > 1;
  const plans = effectiveFilter ? allPlans.filter((p) => p.productCode === effectiveFilter) : allPlans;
  // Default service of a "new package": locked service -> active filter -> platform.
  const newPlanProduct = lockedProductCode ?? productFilter ?? PLATFORM_PRODUCT_CODE;

  function planCountFor(productCode: string): number {
    return allPlans.filter((p) => p.productCode === productCode).length;
  }

  return {
    plans, allPlans, catalog, monitorCatalog, products, productByCode, loading, reload,
    productFilter, setProductFilter, effectiveFilter, showPills, showProductOnCards,
    newPlanProduct, planCountFor,
    deleteTarget, setDeleteTarget, askDelete, deleting, deleteErr, doDelete,
  };
}
